<template>
    <div class="company-member-dropdown">
        <template v-if="editing">
            <VJoyInput
                ref="inputRef"
                v-model="search"
                :placeholder="$t('search.advanced.company.member.dropdown.input.placeholder')"
                name="search"
                icon="search"
                class="company-member-dropdown__input"
            />
            <CompanyMemberDropdownList
                ref="dropdownListRef"
                v-model="model"
                :members="results"
                class="company-member-dropdown__list"
                @item-selected="editing = false"
            />
        </template>
        <VJoyButton v-else variant="secondary" class="company-member-dropdown__button" @click="handleButtonClick">
            <VJoyWrapper no-margin row-gap="2" stretch justify="space-between" wrap="nowrap" class="company-member-dropdown__button__content">
                <VJoyWrapper v-if="model" no-margin wrap="nowrap">
                    <VJoyAvatar :full-name="model.name" :photo-url="model.photo?.absoluteUrl" size="small" />
                    <span>{{ model.name }} - {{ model.companyName }}</span>
                </VJoyWrapper>
                <template v-else>
                    {{ $t('search.advanced.company.member.dropdown.button') }}
                </template>
                <VJoyIcon name="chevron-down" />
            </VJoyWrapper>
        </VJoyButton>
    </div>
</template>

<script setup lang="ts">
    import {nextTick, ref, useTemplateRef, type Ref} from 'vue';
    import {onClickOutside} from '@malt/utils';
    import {VJoyIcon, VJoyButton, VJoyWrapper, VJoyAvatar, VJoyInput} from '@maltjoy/core-vue';
    import type {ClientUserResource} from '@api';
    import CompanyMemberDropdownList from './CompanyMemberDropdownList.vue';
    import {useCompanyClientsForOnBehalfOfSearch} from './composables/useCompanyClientsForOnBehalfOfSearch';

    const props = defineProps<{
        companyId: string;
    }>();

    const model = defineModel<ClientUserResource>();

    const inputRef = useTemplateRef('inputRef');
    const dropdownListRef = useTemplateRef('dropdownListRef');

    const editing = ref(false);

    const {search, results} = useCompanyClientsForOnBehalfOfSearch(props.companyId);

    onClickOutside(dropdownListRef as Ref<HTMLElement | null>, () => (editing.value = false), {ignore: [inputRef as Ref<HTMLElement | null>]});

    async function handleButtonClick() {
        editing.value = true;

        await nextTick(); // Wait for the input element to be mounted
        inputRef.value?.focus();
    }
</script>

<style scoped lang="scss">
    .company-member-dropdown {
        position: relative;
        width: 100%;

        &__input {
            margin-bottom: var(--joy-core-spacing-2);
        }

        &__button {
            width: 100%;
            padding: 0 var(--joy-core-spacing-5);

            :deep(> span) {
                width: 100%;
            }

            &__content {
                width: 100%;
            }
        }

        &__list {
            position: absolute;
            z-index: 2;
            width: 100%;
        }
    }
</style>
